import type { GetStaticProps } from 'next'
import type { FC } from 'react'
import type { Category } from '@/types'

import { Seo } from '@/components/generals/seo'
import { SiteLayout } from '@/components/layout/site-layout'
import { LegalPage } from '@/components/legal/legal-page'

import { getLayoutData } from '@/lib/layout-data'

interface PrivacyProps {
    categories: Category[]
}

const DESCRIPTION =
    'How Milano collects, uses, and protects your personal information when you shop with us.'

const Privacy: FC<PrivacyProps> = ({ categories }) => {
    return (
        <SiteLayout categories={categories}>
            <Seo title='Privacy Policy' description={DESCRIPTION} />

            <LegalPage title='Privacy Policy' updated='June 2026'>
                <p>
                    This Privacy Policy explains how Milano (&quot;we&quot;, &quot;us&quot;, or &quot;our&quot;)
                    collects, uses, and safeguards the information you provide when you visit our website or
                    make a purchase. By using our site, you agree to the practices described below.
                </p>

                <h3>Information We Collect</h3>
                <p>
                    We collect information you provide directly to us — such as your name, email address,
                    shipping address, and payment details — when you create an account, place an order, or
                    contact us. We also collect certain information automatically, including your IP address,
                    browser type, and pages visited, to help us improve our service.
                </p>

                <h3>How We Use Your Information</h3>
                <ul role='list'>
                    <li>To process and fulfil your orders and provide customer support.</li>
                    <li>To send order confirmations, shipping updates, and, where permitted, marketing.</li>
                    <li>To detect and prevent fraud and to keep our site secure.</li>
                    <li>To analyse and improve our products, services, and website experience.</li>
                </ul>

                <h3>Cookies</h3>
                <p>
                    We use cookies and similar technologies to remember your preferences, keep items in your
                    cart, and understand how the site is used. You can disable cookies in your browser
                    settings, though some features may not function correctly as a result.
                </p>

                <h3>Sharing Your Information</h3>
                <p>
                    We do not sell your personal information. We share it only with trusted service providers
                    who help us operate our business — such as payment processors and shipping carriers — and
                    only to the extent necessary to perform those services, or where required by law.
                </p>

                <h3>Your Rights</h3>
                <p>
                    You may request access to, correction of, or deletion of your personal information at any
                    time. To exercise these rights, contact us at
                    {' '}
                    <a href='mailto:hello@milano.clothes'>hello@milano.clothes</a>.
                </p>

                <h3>Contact Us</h3>
                <p>
                    If you have questions about this Privacy Policy, please reach out to us at
                    {' '}
                    <a href='mailto:hello@milano.clothes'>hello@milano.clothes</a>.
                </p>
            </LegalPage>
        </SiteLayout>
    )
}

export const getStaticProps: GetStaticProps<PrivacyProps> = async () => {
    const { categories } = getLayoutData()

    return {
        props: {
            categories
        }
    }
}

export default Privacy
